import React, { useState, useEffect } from "react";
import Index from "../../../Index";
import Switch from "@mui/material/Switch";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Controller } from "react-hook-form";
import {
  getCompanySetupDataApiHandler,
  updateCompanySetupApiHandler,
  getCompanyWhatsappSettingApiHandler,
  saveCompanyWhatsappSettingApiHandler,
} from "../../../../services/AdminIndex";
import {
  WHATSAPP_MODULES,
  isWhatsappModuleOn,
} from "../../../../component/common/WhatsappSendButton";
import invoice1 from "./pdf/invoice1.pdf";
import invoice2 from "./pdf/invoice2.pdf";
import invoice3 from "./pdf/invoice3.pdf";
import invoice4 from "./pdf/invoice4.pdf";

const IOSSwitch = Index.styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 34,
  height: 20,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 3,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.mode === "dark" ? "#2ECA45" : "#114A65",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color:
        theme.palette.mode === "light"
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 14,
    height: 14,
    boxShadow: "none",
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === "light" ? "#E9E9EA" : "#39393D",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));

export default function CompanySetup() {
  const { state } = useLocation();
  const companyId = state?.companyId;

  const dispatch = useDispatch();

  const { loading, token } = Index.useSelector((state) => {
    return state.ERPAdminReducer;
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    watch,
  } = Index.useForm();

  const DEFAULT_COMPANY_ACCESS = {
    modules: {
      purchase: false,
      sales: false,
      accounting: false,
      process: false,
    },
    utility: {
      telly: false,
      dispatchAdvice: false,
      whatsappIntegration: false,
      emailIntegration: false,
      ewaybill: false,
      einvoice: false,
    },
    Invoice1: false,
    Invoice2: false,
    Invoice3: false,
    Invoice4: false,
  };

  const [companyAccess, setCompanyAccess] = useState(DEFAULT_COMPANY_ACCESS);

  const [moduleList, setModuleList] = useState([]);
  // Company's own WhatsApp gateway instance. The saved token never comes
  // back from the server - only its mask; a blank field keeps it.
  const [whatsappSetting, setWhatsappSetting] = useState({
    hasToken: false,
    tokenMasked: "",
    instanceToken: "",
    senderNumber: "",
    balance: "",
  });
  const [balanceLoader, setBalanceLoader] = useState(false);
  const [assignedModuleTags, setAssignedModuleTags] = useState([]);

  const invoiceFields = ["Invoice1", "Invoice2", "Invoice3", "Invoice4"];

  const toggleModuleAssignment = (moduleTag) => {
    setAssignedModuleTags((prev) =>
      prev.includes(moduleTag)
        ? prev.filter((tag) => tag !== moduleTag)
        : [...prev, moduleTag]
    );
  };

  const handleInvoiceChange = (name, checked) => {
    if (checked) {
      const checkedCount = invoiceFields.filter(
        (field) => field !== name && watch(field)
      ).length;
      if (checkedCount >= 2) {
        toast.error("You can select maximum 2 invoice formats");
        return;
      }
    }
    setValue(name, checked);
  };

  useEffect(() => {
    getCompanySetupData();
    getWhatsappSetting();
  }, [companyId]);

  const getWhatsappSetting = () => {
    if (!companyId) return;
    getCompanyWhatsappSettingApiHandler(token, companyId).then((response) => {
      if (response.status === 201 || response.status === 200) {
        setWhatsappSetting({
          hasToken: !!response?.data?.hasToken,
          tokenMasked: response?.data?.tokenMasked || "",
          instanceToken: "",
          senderNumber: response?.data?.senderNumber || "",
          balance: response?.data?.lastBalance || "",
        });
      }
    });
  };

  const handleCheckBalance = () => {
    if (!whatsappSetting.hasToken && !whatsappSetting.instanceToken.trim()) {
      toast.error("Please enter the Instance ID (Token)");
      return;
    }
    setBalanceLoader(true);
    saveCompanyWhatsappSettingApiHandler(token, {
      companyId,
      action: "checkBalance",
      instanceToken: whatsappSetting.instanceToken.trim(),
    }).then((response) => {
      setBalanceLoader(false);
      if (response.status === 201 || response.status === 200) {
        setWhatsappSetting((prev) => ({
          ...prev,
          balance: response?.data?.balance || "",
          // The number linked on the gateway, when not typed in yet.
          senderNumber: prev.senderNumber || response?.data?.connectedNumber || "",
        }));
      } else {
        dispatch({ type: "ADMIN_ERROR_HANDLER", payload: response });
      }
    });
  };

  // Saved together with Company Setup when WhatsApp Integration is on.
  const saveWhatsappSetting = () =>
    saveCompanyWhatsappSettingApiHandler(token, {
      companyId,
      instanceToken: whatsappSetting.instanceToken.trim(),
      senderNumber: whatsappSetting.senderNumber,
      isEnabled: true,
    }).then((response) => {
      if (response.status === 201 || response.status === 200) {
        setWhatsappSetting((prev) => ({
          ...prev,
          hasToken: !!response?.data?.hasToken,
          tokenMasked: response?.data?.tokenMasked || "",
          instanceToken: "",
        }));
        return true;
      }
      dispatch({ type: "ADMIN_ERROR_HANDLER", payload: response });
      return false;
    });

  const getCompanySetupData = () => {
    try {
      getCompanySetupDataApiHandler(token, companyId).then((data) => {
        if (data.status === 201 || data.status === 200) {
          setModuleList(data.modules || []);
          setAssignedModuleTags(data.assignedModules || []);
          if (data.data?.[0]?.id) {
            setValue("Invoice1", data.data?.[0]?.invoice1);
            setValue("Invoice2", data.data?.[0]?.invoice2);
            setValue("Invoice3", data.data?.[0]?.invoice3);
            setValue("Invoice4", data.data?.[0]?.invoice4);
            let parsedAccess = {};
            try {
              parsedAccess = JSON.parse(data.data?.[0]?.access) || {};
            } catch (e) {
              parsedAccess = {};
            }
            // Merge onto defaults instead of replacing — a freshly created
            // company's access JSON only carries "assignedModules" (written
            // by CreateCompany), so a plain replace would drop the
            // modules/utility sub-objects that updateAccess("utility.x", …)
            // below assumes exist.
            setCompanyAccess({
              ...DEFAULT_COMPANY_ACCESS,
              ...parsedAccess,
              modules: { ...DEFAULT_COMPANY_ACCESS.modules, ...parsedAccess.modules },
              utility: { ...DEFAULT_COMPANY_ACCESS.utility, ...parsedAccess.utility },
            });
            setValue("ewayBillUsername", data.data?.[0]?.ewayBillUsername);
            setValue("ewayBillPassword", data.data?.[0]?.ewayBillPassword);
            setValue(
              "einvoiceBillUsername",
              data.data?.[0]?.einvoiceBillUsername
            );
            setValue(
              "einvoiceBillPassword",
              data.data?.[0]?.einvoiceBillPassword
            );
          }
        } else {
          dispatch({ type: "ADMIN_ERROR_HANDLER", payload: data });
        }
      });
    } catch (error) {
    }
  };

  // Writes the full map on first toggle, so an unsaved map (= all on)
  // keeps the other modules on.
  const toggleWhatsappModule = (moduleKey) => {
    setCompanyAccess((prevState) => {
      const utility = prevState.utility || {};
      const modules = {};
      WHATSAPP_MODULES.forEach((m) => {
        modules[m.key] = isWhatsappModuleOn(utility, m.key);
      });
      modules[moduleKey] = !modules[moduleKey];
      return { ...prevState, utility: { ...utility, whatsappModules: modules } };
    });
  };

  const updateAccess = (propertyPath, value) => {
    setCompanyAccess((prevState) => {
      const properties = propertyPath.split(".");
      const newState = { ...prevState };
      let target = newState;
      for (let i = 0; i < properties.length - 1; i++) {
        target = target[properties[i]];
      }
      target[properties[properties.length - 1]] = value;
      return newState;
    });
  };

  const submit = (data) => {
    const whatsappOn = !!companyAccess?.utility?.whatsappIntegration;
    if (whatsappOn && !whatsappSetting.hasToken && !whatsappSetting.instanceToken.trim()) {
      toast.error("Please enter the WhatsApp Instance ID (Token)");
      return;
    }
    data.invoice1 = data.Invoice1;
    data.invoice2 = data.Invoice2;
    data.invoice3 = data.Invoice3;
    data.invoice4 = data.Invoice4;
    const accessData = {
      ...companyAccess,
      Invoice1: !!data.Invoice1,
      Invoice2: !!data.Invoice2,
      Invoice3: !!data.Invoice3,
      Invoice4: !!data.Invoice4,
      assignedModules: assignedModuleTags,
    };
    data.access = JSON.stringify(accessData);
    data.companyId = companyId;
    // With the switch on, the Instance ID is saved first; if that fails
    // nothing else is saved, so the switch never ends up on without it.
    // Switch off = the saved token is kept but not used (the server checks
    // the switch before every send).
    (whatsappOn ? saveWhatsappSetting() : Promise.resolve(true)).then((whatsappSaved) => {
      if (!whatsappSaved) return;
      updateCompanySetupApiHandler(token, data).then((response) => {
        if (response.status === 201 || response.status === 200) {
          toast.success("Setting updated successfully");
        } else {
          dispatch({ type: "ADMIN_ERROR_HANDLER", payload: response });
        }
      });
    });
  };

  // One labelled switch tile; used by every toggle list on this screen.
  const renderSwitchTile = (key, label, checked, onToggle) => (
    <Index.Box className="company-setup-tile" key={key}>
      <Index.Typography className="company-switch-lable">{label}</Index.Typography>
      <Index.FormControlLabel
        onChange={onToggle}
        control={<IOSSwitch sx={{ m: 1 }} checked={!!checked} />}
      />
    </Index.Box>
  );

  // Section heading with an on/off switch on the right.
  const renderSectionHead = (title, checked, onToggle) => (
    <Index.Box className="company-setup-section-head">
      <Index.Typography className="company-setup-section-title">{title}</Index.Typography>
      <Index.FormControlLabel
        onChange={onToggle}
        control={<IOSSwitch sx={{ m: 1 }} checked={!!checked} />}
      />
    </Index.Box>
  );

  const renderTextField = (label, name, required) => (
    <Index.Box
      gridColumn={{ xs: "span 12", sm: "span 6", md: "span 4", lg: "span 3" }}
      className="grid-column"
    >
      <Index.Box className="input-box">
        <Index.Box className="form-group">
          <Index.FormHelperText className="lable-control">{label}</Index.FormHelperText>
          <Index.TextField
            fullWidth
            className="custom-form-control"
            value={watch(name) || ""}
            {...register(name, { required: !!required })}
            onChange={(e) => setValue(name, e.target.value)}
            error={Boolean(errors[name])}
          />
        </Index.Box>
      </Index.Box>
    </Index.Box>
  );

  const invoicePdfs = { Invoice1: invoice1, Invoice2: invoice2, Invoice3: invoice3, Invoice4: invoice4 };

  return (
    <>
      <Index.Box className="main_content">
        <Index.Box className="page-title-main">
          <Index.Box className="flex-justify">
            <Index.Typography variant="h4" component="h4">
              Company Setup
            </Index.Typography>
          </Index.Box>
        </Index.Box>

        <Index.Box className="web-main-content">
          <Index.Box className="over-text-scroll">
            <form className="form-content company-setup-page" onSubmit={handleSubmit(submit)}>
              <Index.Box className="common-card m-30">
                <Index.Typography className="company-setup-section-title company-setup-section-gap">
                  Company Modules
                </Index.Typography>
                <Index.Box className="company-setup-tile-grid">
                  {moduleList.map((module) =>
                    renderSwitchTile(
                      module.tag,
                      module.title,
                      assignedModuleTags.includes(module.tag),
                      () => toggleModuleAssignment(module.tag)
                    )
                  )}
                </Index.Box>
              </Index.Box>

              <Index.Box className="common-card m-30">
                <Index.Typography className="company-setup-section-title company-setup-section-gap">
                  Utility
                </Index.Typography>
                <Index.Box className="company-setup-tile-grid">
                  {renderSwitchTile("telly", "Tally", companyAccess?.utility?.telly, () =>
                    updateAccess("utility.telly", !companyAccess?.utility?.telly)
                  )}
                  {/* Turns on the Allocation Planner -> Dispatch Advice flow:
                      both menus, their Role Master permissions and APIs
                      (the company also needs the Sales module). */}
                  {renderSwitchTile(
                    "dispatchAdvice",
                    "Dispatch Advice",
                    companyAccess?.utility?.dispatchAdvice,
                    () => updateAccess("utility.dispatchAdvice", !companyAccess?.utility?.dispatchAdvice)
                  )}
                  {renderSwitchTile(
                    "emailIntegration",
                    "Email Integration",
                    companyAccess?.utility?.emailIntegration,
                    () => updateAccess("utility.emailIntegration", !companyAccess?.utility?.emailIntegration)
                  )}
                </Index.Box>
              </Index.Box>

              <Index.Box className="common-card m-30">
                {renderSectionHead(
                  "WhatsApp Integration",
                  companyAccess?.utility?.whatsappIntegration,
                  () => updateAccess("utility.whatsappIntegration", !companyAccess?.utility?.whatsappIntegration)
                )}
                {companyAccess?.utility?.whatsappIntegration && (
                  <>
                    <Index.Box sx={{ width: 1 }} className="grid-main company-setup-section-body">
                      <Index.Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={{ xs: 2, sm: 2, md: 2, lg: 2 }}>
                        <Index.Box
                          gridColumn={{ xs: "span 12", sm: "span 6", md: "span 4", lg: "span 3" }}
                          className="grid-column"
                        >
                          <Index.Box className="input-box">
                            <Index.Box className="form-group">
                              <Index.FormHelperText className="lable-control">
                                Instance ID (Token)
                              </Index.FormHelperText>
                              <Index.TextField
                                fullWidth
                                type="password"
                                autoComplete="new-password"
                                className="custom-form-control"
                                placeholder={whatsappSetting.tokenMasked || "Instance ID"}
                                value={whatsappSetting.instanceToken}
                                onChange={(e) =>
                                  setWhatsappSetting((prev) => ({
                                    ...prev,
                                    instanceToken: e.target.value,
                                  }))
                                }
                              />
                            </Index.Box>
                          </Index.Box>
                        </Index.Box>
                        <Index.Box
                          gridColumn={{ xs: "span 12", sm: "span 6", md: "span 4", lg: "span 3" }}
                          className="grid-column"
                        >
                          <Index.Box className="input-box">
                            <Index.Box className="form-group">
                              <Index.FormHelperText className="lable-control">
                                WhatsApp Number
                              </Index.FormHelperText>
                              <Index.TextField
                                fullWidth
                                className="custom-form-control"
                                placeholder="91xxxxxxxxxx"
                                value={whatsappSetting.senderNumber}
                                inputProps={{ maxLength: 12 }}
                                onChange={(e) =>
                                  setWhatsappSetting((prev) => ({
                                    ...prev,
                                    senderNumber: e.target.value.replace(/[^0-9]/g, ""),
                                  }))
                                }
                              />
                            </Index.Box>
                          </Index.Box>
                        </Index.Box>
                        <Index.Box
                          gridColumn={{ xs: "span 12", sm: "span 12", md: "span 4", lg: "span 6" }}
                          className="grid-column company-setup-balance"
                        >
                          <Index.LinearButton
                            btnLabel={balanceLoader ? "Checking..." : "Check Balance"}
                            className="btn-primary"
                            disabled={balanceLoader}
                            onClick={handleCheckBalance}
                          ></Index.LinearButton>
                          {whatsappSetting.balance ? (
                            <Index.Typography className="company-switch-lable">
                              Balance: {whatsappSetting.balance}
                            </Index.Typography>
                          ) : null}
                        </Index.Box>
                      </Index.Box>
                    </Index.Box>
                    <Index.Typography className="company-setup-sub-title">
                      WhatsApp Modules
                    </Index.Typography>
                    <Index.Box className="company-setup-tile-grid">
                      {WHATSAPP_MODULES.map((module) =>
                        renderSwitchTile(
                          module.key,
                          module.title,
                          isWhatsappModuleOn(companyAccess?.utility, module.key),
                          () => toggleWhatsappModule(module.key)
                        )
                      )}
                    </Index.Box>
                  </>
                )}
              </Index.Box>

              <Index.Box className="common-card m-30">
                {renderSectionHead("Eway Bill", companyAccess?.utility?.ewaybill, () =>
                  updateAccess("utility.ewaybill", !companyAccess?.utility?.ewaybill)
                )}
                {companyAccess?.utility?.ewaybill && (
                  <Index.Box sx={{ width: 1 }} className="grid-main company-setup-section-body">
                    <Index.Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={{ xs: 2, sm: 2, md: 2, lg: 2 }}>
                      {renderTextField("Username", "ewayBillUsername", companyAccess?.utility?.ewaybill)}
                      {renderTextField("Password", "ewayBillPassword", companyAccess?.utility?.ewaybill)}
                    </Index.Box>
                  </Index.Box>
                )}
              </Index.Box>

              <Index.Box className="common-card m-30">
                {renderSectionHead("E - Invoice", companyAccess?.utility?.einvoice, () =>
                  updateAccess("utility.einvoice", !companyAccess?.utility?.einvoice)
                )}
                {companyAccess?.utility?.einvoice && (
                  <Index.Box sx={{ width: 1 }} className="grid-main company-setup-section-body">
                    <Index.Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={{ xs: 2, sm: 2, md: 2, lg: 2 }}>
                      {renderTextField("Username", "einvoiceBillUsername", companyAccess?.utility?.einvoice)}
                      {renderTextField("Password", "einvoiceBillPassword", companyAccess?.utility?.einvoice)}
                    </Index.Box>
                  </Index.Box>
                )}
              </Index.Box>

              <Index.Box className="common-card m-30">
                <Index.Typography className="company-setup-section-title company-setup-section-gap">
                  Print Invoice
                </Index.Typography>
                <Index.Box className="company-setup-tile-grid">
                  {invoiceFields.map((name) => (
                    <Index.Box className="company-setup-tile" key={name}>
                      <Index.Box className="checkbox-content-flex">
                        <Index.Box className="checkbox-main">
                          <Controller
                            name={name}
                            control={control}
                            render={({ field }) => (
                              <input
                                type="checkbox"
                                id={`company-setup-${name}`}
                                className="checkbox"
                                checked={!!field.value}
                                onChange={() => handleInvoiceChange(name, !field.value)}
                              />
                            )}
                          />
                        </Index.Box>
                        <label htmlFor={`company-setup-${name}`} className="company-switch-lable">
                          {name.replace("Invoice", "Invoice ")}
                        </label>
                      </Index.Box>
                      <Index.LinearButton
                        btnLabel="Preview"
                        className="btn-primary"
                        onClick={() => window.open(invoicePdfs[name], "_blank")}
                      ></Index.LinearButton>
                    </Index.Box>
                  ))}
                </Index.Box>
              </Index.Box>

              <Index.Box className="btn-flex-end">
                <Index.Box className="btn-primary-main">
                  <Index.Button variant="" className="btn-primary" type="submit">
                    Save
                  </Index.Button>
                </Index.Box>
              </Index.Box>
            </form>
          </Index.Box>
        </Index.Box>
      </Index.Box>
    </>
  );
}
